import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { imgUrl } from "@/lib/glossyApi";
import { igUrl, ttUrl, liUrl, fbUrl, webUrl } from "@/lib/socialLinks";
import { MapPin, Phone, Mail, Globe, Instagram, Linkedin, Facebook, ArrowRight, ShoppingBag } from "lucide-react";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.9 2.9 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.04.86.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
  </svg>
);

// The point of every feature: readers must know what they do, what they sell,
// who it's for and exactly how to buy, book or enquire.
export const MemberContactBlock = ({ memberId, accent }) => {
  const [m, setM] = useState(null);
  useEffect(() => {
    if (!memberId) return;
    client.get(`/members/${memberId}`).then((r) => setM(r.data)).catch(() => {});
  }, [memberId]);
  if (!m) return null;

  const phone = m.show_mobile !== false && m.mobile ? m.mobile : m.show_landline !== false && m.landline ? m.landline : null;
  const email = m.show_email !== false ? m.email : null;
  const site = webUrl(m.website);
  const shop = webUrl(m.shop_url);
  const ig = igUrl(m.instagram);
  const li = liUrl(m.linkedin);
  const fb = fbUrl(m.facebook);
  const tt = ttUrl(m.tiktok);
  const showSite = site && (!shop || site.replace(/\/+$/, "") !== shop.replace(/\/+$/, ""));
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: m.business_name,
    description: m.services || m.bio || undefined,
    address: m.display_address !== false && m.address ? { "@type": "PostalAddress", streetAddress: m.address, addressLocality: m.borough, addressCountry: "GB" } : undefined,
    telephone: phone || undefined,
    email: email || undefined,
    url: site || undefined,
    image: m.directory_image ? imgUrl({ cover_image: m.directory_image }) : undefined,
    sameAs: [ig, li, fb, tt].filter(Boolean),
  };

  return (
    <div className="mt-14 border border-ink/15 bg-white" data-testid="member-contact-block">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="h-1.5" style={{ backgroundColor: accent || m.accent_color || "#b9974a" }} />
      <div className="p-6 sm:p-8">
        <p className="overline text-ink/50 mb-2">Choose {m.business_name}</p>
        {m.services && <p className="font-serif text-xl sm:text-2xl tracking-tight leading-snug">{m.services}</p>}
        {m.offers && (
          <p className="font-sans text-sm bg-gloss-gold/15 border border-gloss-gold/40 px-4 py-3 mt-4" data-testid="contact-offer">
            <b>Reader offer:</b> {m.offers}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6 font-sans text-sm text-ink/80">
          {m.display_address !== false && m.address && <p className="flex items-start gap-2.5" data-testid="contact-address"><MapPin size={15} className="mt-0.5 shrink-0 text-ink/40" /> {m.address}{m.borough ? `, ${m.borough}` : ""}</p>}
          {phone && <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-2.5 hover:text-ink transition-colors" data-testid="contact-phone"><Phone size={15} className="shrink-0 text-ink/40" /> {phone}</a>}
          {email && <a href={`mailto:${email}`} className="flex items-center gap-2.5 hover:text-ink transition-colors" data-testid="contact-email"><Mail size={15} className="shrink-0 text-ink/40" /> {email}</a>}
          {shop && <a href={shop} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 hover:text-ink transition-colors" data-testid="contact-shop"><ShoppingBag size={15} className="shrink-0 text-ink/40" /> {shop.replace(/^https?:\/\/(www\.)?/, "").replace(/\/+$/, "")}</a>}
          {showSite && <a href={site} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 hover:text-ink transition-colors" data-testid="contact-website"><Globe size={15} className="shrink-0 text-ink/40" /> {site.replace(/^https?:\/\/(www\.)?/, "")}</a>}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-7">
          {site && (
            <a href={site} target="_blank" rel="noreferrer" className="overline text-cream px-6 py-3.5 inline-flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ backgroundColor: accent || m.accent_color || "#141414" }} data-testid="contact-cta">
              Visit {m.business_name} <ArrowRight size={14} />
            </a>
          )}
          <Link to={`/member/${m.id}`} className="overline border border-ink px-6 py-3.5 hover:bg-ink hover:text-cream transition-colors" data-testid="contact-profile-link">Full profile &amp; features</Link>
          {ig && <a href={ig} target="_blank" rel="noreferrer" className="p-2.5 text-ink/50 hover:text-ink transition-colors" data-testid="contact-instagram"><Instagram size={18} /></a>}
          {tt && <a href={tt} target="_blank" rel="noreferrer" className="p-2.5 text-ink/50 hover:text-ink transition-colors" data-testid="contact-tiktok"><TikTokIcon /></a>}
          {fb && <a href={fb} target="_blank" rel="noreferrer" className="p-2.5 text-ink/50 hover:text-ink transition-colors" data-testid="contact-facebook"><Facebook size={18} /></a>}
          {li && <a href={li} target="_blank" rel="noreferrer" className="p-2.5 text-ink/50 hover:text-ink transition-colors" data-testid="contact-linkedin"><Linkedin size={18} /></a>}
        </div>
      </div>
    </div>
  );
};
